import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import { newsroomArticles, getStoredNewsroomState, saveNewsroomState } from './newsroomData';
import {
  ArrowLeft, Eye, Heart, MessageSquare, Maximize2, Minimize2, ZoomIn, ChevronLeft, ChevronRight, X, Link as LinkIcon, MoreVertical, Share2
} from 'lucide-react';
import './NewsroomDetail.css';

// SVG Brand Icons
const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const TwitterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const LinkedinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.25V10.9H6.46M7.86 6.74a1.62 1.62 0 1 0 0 3.24 1.62 1.62 0 0 0 0-3.24z"/>
  </svg>
);

export default function NewsroomDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [appState, setAppState] = useState(getStoredNewsroomState);
  const [copiedLink, setCopiedLink] = useState(false);
  const [threeDotsOpen, setThreeDotsOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [copiedToast, setCopiedToast] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
      setIsFullscreen(false);
    }
  };
  const [commentsList, setCommentsList] = useState([]);
  const [newCommentText, setNewCommentText] = useState('');

  const currentIndex = newsroomArticles.findIndex((a) => a.id === String(id));
  const article = newsroomArticles[currentIndex];

  const viewIncrementedRef = React.useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Increment view count exactly ONCE when article page is opened/visited
    if (id && viewIncrementedRef.current !== id) {
      viewIncrementedRef.current = id;
      setAppState((prev) => {
        const currentViews = prev.viewsMap[id] || 0;
        const nextState = {
          ...prev,
          viewsMap: {
            ...prev.viewsMap,
            [id]: currentViews + 1,
          },
        };
        saveNewsroomState(nextState);
        return nextState;
      });
    }
  }, [id]);

  // Click listener delegation ONLY for image container (.article-image-container), excluding caption text
  useEffect(() => {
    const handleContainerClick = (e) => {
      if (e.target.closest('a')) return;
      const imgContainer = e.target.closest('.article-image-container');
      if (imgContainer) {
        const figure = imgContainer.closest('.article-image-figure');
        if (figure) {
          const idxStr = figure.getAttribute('data-img-idx');
          const idx = idxStr !== null ? parseInt(idxStr, 10) : 0;
          openLightbox(idx);
        }
      }
    };
    const el = document.querySelector('.article-html-content');
    if (el) {
      el.addEventListener('click', handleContainerClick);
      return () => el.removeEventListener('click', handleContainerClick);
    }
  }, [id, article]);

  if (!article) {
    return (
      <div className="newsroom-detail-wrapper">
        <Navbar />
        <main className="newsroom-detail-container">
          <button className="back-to-newsroom-btn" onClick={() => navigate('/newsroom')}>
            <ArrowLeft size={16} /> Back to Newsroom
          </button>
          <div className="not-found-card">
            <h2>Article Not Found</h2>
            <p>The requested newsroom article does not exist or has been removed.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const viewsCount = appState.viewsMap[article.id] ?? article.views;
  const likesCount = appState.likesMap[article.id] ?? article.likes;
  const isLiked = !!appState.userLikedMap[article.id];

  // Dynamic Recent Posts: Exclude current article, take top 3 newest articles
  const recentPosts = newsroomArticles
    .filter((a) => a.id !== String(id))
    .slice(0, 3);

  const handleToggleLike = () => {
    setAppState((prev) => {
      const currentlyLiked = prev.userLikedMap[article.id];
      const curLikes = prev.likesMap[article.id] || 0;
      const nextState = {
        ...prev,
        userLikedMap: {
          ...prev.userLikedMap,
          [article.id]: !currentlyLiked,
        },
        likesMap: {
          ...prev.likesMap,
          [article.id]: currentlyLiked ? curLikes - 1 : curLikes + 1,
        },
      };
      saveNewsroomState(nextState);
      return nextState;
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopiedLink(true);
      setCopiedToast(true);
      setTimeout(() => {
        setCopiedLink(false);
        setCopiedToast(false);
      }, 2500);
    });
  };

  const handleSharePlatform = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(article.title);
    let shareUrl = '';

    if (platform === 'facebook') {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    } else if (platform === 'twitter') {
      shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
    } else if (platform === 'linkedin') {
      shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const openShareModal = () => {
    setShareModalOpen(true);
    setThreeDotsOpen(false);
  };

  const closeShareModal = () => {
    setShareModalOpen(false);
    setCopiedToast(false);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setIsZoomed(false);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setIsZoomed(false);
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  };

  const prevImage = () => {
    setIsZoomed(false);
    setLightboxIndex((prev) => (prev - 1 + article.images.length) % article.images.length);
  };

  const nextImage = () => {
    setIsZoomed(false);
    setLightboxIndex((prev) => (prev + 1) % article.images.length);
  };

  const handleAddComment = () => {
    if (!newCommentText.trim()) return;
    const newComment = {
      text: newCommentText.trim(),
      time: 'Just now'
    };
    setCommentsList((prev) => [...prev, newComment]);
    setNewCommentText('');
  };

  return (
    <div className="newsroom-detail-wrapper">
      <Navbar />

      <main className="newsroom-detail-container">
        <button className="back-to-newsroom-btn" onClick={() => navigate('/newsroom')}>
          <ArrowLeft size={16} /> Back to Newsroom
        </button>

        {/* A4 Sheet Document Style Article Container */}
        <article className={`a4-article-sheet ${article.id === '21' ? 'article-21-sheet' : ''} ${article.id === '22' ? 'article-22-sheet' : ''} ${article.id === '23' ? 'article-23-sheet' : ''} ${article.id === '24' ? 'article-24-sheet' : ''} ${article.id === '26' ? 'article-26-sheet' : ''} ${article.id === '27' ? 'article-27-sheet' : ''} ${article.id === '28' ? 'article-28-sheet' : ''} ${article.id === '30' ? 'article-30-sheet' : ''} ${article.id === '32' ? 'article-32-sheet' : ''} ${article.id === '33' ? 'article-33-sheet' : ''} ${article.id === '34' ? 'article-34-sheet' : ''} ${article.id === '35' ? 'article-35-sheet' : ''} ${article.id === '36' ? 'article-36-sheet' : ''}`}>
          {/* Article Meta Header Row: Date & Read Time on Left, Three Dots Share on Right */}
          <div className="article-meta-row">
            <div className="article-meta-eyebrow">
              {article.date} · {article.readTime}
            </div>

            <div className="three-dots-wrapper" style={{ position: 'relative' }}>
              <button
                className="three-dots-btn"
                onClick={() => setThreeDotsOpen(!threeDotsOpen)}
                aria-label="More options"
                title="Share Post Options"
              >
                <MoreVertical size={18} />
              </button>

              {threeDotsOpen && (
                <div className="three-dots-menu">
                  <button
                    className="menu-item-btn"
                    onClick={openShareModal}
                  >
                    <Share2 size={15} />
                    <span>Share Post</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Main Article Title (formatted in 2 rows) */}
          <h1 className="a4-article-title">{article.title}</h1>

          {/* Article Body Content */}
          <div className="a4-article-body">
            {/* HTML / Body Paragraphs & Headings with Inline Images */}
            <div
              className="article-html-content"
              dangerouslySetInnerHTML={{ __html: article.fullContent }}
            />
          </div>

          {/* Social Share Bar */}
          <div className="article-social-share-bar">
            <span className="share-bar-label">Share this post:</span>
            <div className="social-icon-btns">
              <button
                className="social-share-icon-btn facebook"
                onClick={() => handleSharePlatform('facebook')}
                title="Share on Facebook"
              >
                <FacebookIcon />
              </button>
              <button
                className="social-share-icon-btn twitter"
                onClick={() => handleSharePlatform('twitter')}
                title="Share on X"
              >
                <TwitterIcon />
              </button>
              <button
                className="social-share-icon-btn linkedin"
                onClick={() => handleSharePlatform('linkedin')}
                title="Share on LinkedIn"
              >
                <LinkedinIcon />
              </button>
              <button
                className="social-share-icon-btn copy-link"
                onClick={handleCopyLink}
                title="Copy Link"
              >
                <LinkIcon size={18} />
              </button>
            </div>
            {copiedLink && <span className="copied-toast-inline">Link Copied!</span>}
          </div>

          {/* Views / Comments / Like Engagement Section */}
          <div className="article-engagement-bar">
            <div className="engagement-left-stats">
              <span>{viewsCount} views</span>
              <span className="bullet-dot">•</span>
              <span>{article.commentsCount + commentsList.length} comments</span>
            </div>

            <button
              className={`engagement-like-btn ${isLiked ? 'liked' : ''}`}
              onClick={handleToggleLike}
            >
              <Heart size={18} />
              <span>{likesCount}</span>
            </button>
          </div>
        </article>

        {/* Recent Posts Section (+3 Next Articles) */}
        <section className="recent-posts-section">
          <div className="recent-posts-header">
            <h3 className="recent-posts-heading">Recent Posts</h3>
            <a className="see-all-link" onClick={() => navigate('/newsroom')}>See All</a>
          </div>

          <div className="recent-posts-grid">
            {recentPosts.map((post) => {
              const postViews = appState.viewsMap[post.id] ?? post.views;
              const postLikes = appState.likesMap[post.id] ?? post.likes;
              const postLiked = !!appState.userLikedMap[post.id];

              return (
                <div
                  key={post.id}
                  className="recent-post-card"
                  onClick={() => navigate(`/newsroom/${post.id}`)}
                >
                  <div className="recent-post-thumb-wrapper">
                    <img src={post.imageUrl} alt={post.title} className="recent-post-thumb" />
                  </div>
                  <div className="recent-post-info">
                    <h4 className="recent-post-title">{post.title}</h4>
                    <div className="recent-post-meta">
                      <div className="recent-meta-stats">
                        <span className="meta-item"><MessageSquare size={14} /> {post.commentsCount}</span>
                      </div>
                      <span className={`recent-like-icon ${postLiked ? 'liked' : ''}`}>
                        <Heart size={15} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Comments Box Section */}
        <section className="comments-box-section">
          <h3 className="comments-heading">Comments</h3>
          <hr className="comments-divider" />

          <div className="comment-input-group">
            <textarea
              className="comment-textarea"
              placeholder="Write a comment..."
              value={newCommentText}
              onChange={(e) => setNewCommentText(e.target.value)}
              rows={3}
            />
            <button className="publish-comment-btn" onClick={handleAddComment}>
              Publish
            </button>
          </div>

          {commentsList.length > 0 && (
            <div className="comments-list">
              {commentsList.map((c, i) => (
                <div key={i} className="comment-item">
                  <div className="comment-author">Seller</div>
                  <div className="comment-text">{c.text}</div>
                  <div className="comment-time">{c.time}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* FULL-SCREEN LIGHTBOX MODAL (WHITE BACKDROP) */}
      {lightboxOpen && article.images && article.images.length > 0 && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button
            className="lightbox-control-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Full Screen"}
          >
            {isFullscreen ? <Minimize2 size={24} /> : <Maximize2 size={24} />}
          </button>

          <button className="lightbox-close-btn" onClick={closeLightbox} aria-label="Close">
            <X size={32} />
          </button>

          {article.images.length > 1 && (
            <button
              className="lightbox-nav-btn prev"
              onClick={(e) => {
                e.stopPropagation();
                prevImage();
              }}
              aria-label="Previous Image"
            >
              <ChevronLeft size={44} />
            </button>
          )}

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={article.images[lightboxIndex].url}
              alt={article.images[lightboxIndex].caption || article.title}
              className={`lightbox-img ${isZoomed ? 'zoomed' : ''}`}
            />
            {article.images[lightboxIndex].caption && (
              <p className="lightbox-caption">{article.images[lightboxIndex].caption}</p>
            )}
          </div>

          {article.images.length > 1 && (
            <button
              className="lightbox-nav-btn next"
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              aria-label="Next Image"
            >
              <ChevronRight size={44} />
            </button>
          )}
        </div>
      )}

      {/* SHARE POST POPUP MODAL */}
      {shareModalOpen && (
        <div className="share-modal-overlay" onClick={closeShareModal}>
          <div
            className="share-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="share-modal-close-btn"
              onClick={closeShareModal}
              aria-label="Close modal"
            >
              <X size={18} />
            </button>

            <h3 className="share-modal-title">Share Post</h3>

            <div className="share-icons-row">
              <button
                className="share-icon-btn share-btn-facebook"
                onClick={() => handleSharePlatform('facebook')}
                title="Share on Facebook"
              >
                <FacebookIcon />
              </button>

              <button
                className="share-icon-btn share-btn-twitter"
                onClick={() => handleSharePlatform('twitter')}
                title="Share on X (Twitter)"
              >
                <TwitterIcon />
              </button>

              <button
                className="share-icon-btn share-btn-linkedin"
                onClick={() => handleSharePlatform('linkedin')}
                title="Share on LinkedIn"
              >
                <LinkedinIcon />
              </button>

              <button
                className="share-icon-btn share-btn-copy"
                onClick={handleCopyLink}
                title="Copy Link"
              >
                <LinkIcon size={22} />
              </button>
            </div>

            {copiedToast && (
              <div className="copy-toast">
                ✓ Link copied to clipboard!
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
